import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createScoreboardStyles } from '../styles/ScoreBoard.styles';
import { getAssetManifest } from '@core/UI/utils/getAssetHelper';
import { getGameFromUrl } from '@core/utils/urlParser';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import { useT } from '@core/i18n';
import { SkyxNewModalPanelShell } from '@core/UI/skyx/SkyxNewModalPanelShell';
import { WalletBalance } from '@core/UI/wallet/WalletBalance';
import { PF_HEADER_WALLET_RESPONSIVE_CSS } from '@core/UI/wallet/ProvablyFairHeaderResponsive.css';

interface AssetManager {
  baseUrl: string;
}

interface ProvablyFairModalProps {
  isOpen: boolean;
  onClose: () => void;
  assetManager: AssetManager | null;
  styles: ReturnType<typeof createScoreboardStyles>;
  /** When set, strings resolve from this game bundle first (e.g. skyx_new). */
  i18nGameId?: string;
  /** Optional coded diagram (e.g. skyx_new horizontal PF flow). */
  diagramOverride?: React.ReactNode;
  /** Optional header icon URL (replaces default provably fair icon). */
  headerIconUrl?: string;
}

const PF = 'ui.popuppanel.provably_fair';

export const ProvablyFairInfoModal: FC<ProvablyFairModalProps> = ({
  isOpen,
  onClose,
  assetManager,
  styles,
  i18nGameId,
  diagramOverride,
  headerIconUrl,
}) => {
  const t = useT(i18nGameId);
  const [visible, setVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setVisible(true), 10);
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  const gameSlug = getGameFromUrl() || 'crash';
  const assetFolder = resolveAssetFolderSlug(gameSlug) || gameSlug;
  const manifest = getAssetManifest(gameSlug);
  const PROVABLY_FAIR_ICON = manifest.images.provablyfairicon2;
  const DIAGRAM_IMAGE =
    manifest.images.provablyfairdiagram || 'default/provably_fair_diagram.png';

  const modalIconUrl = buildGamePublicAssetUrl(
    assetFolder,
    PROVABLY_FAIR_ICON,
    assetManager?.baseUrl
  );

  const diagramImageUrl = buildGamePublicAssetUrl(
    assetFolder,
    DIAGRAM_IMAGE,
    assetManager?.baseUrl
  );

  const collectBullets = (suffixes: readonly string[]) =>
    suffixes
      .map(s => (t(`${PF}.${s}`, { defaultValue: '' }) as string).trim())
      .filter(Boolean);

  const introBullets = collectBullets(['intro_bullet_1', 'intro_bullet_2']);
  const quickBullets = collectBullets([
    'quick_bullet_1',
    'quick_bullet_2',
    'quick_bullet_3',
  ]);
  const generatedInputs = collectBullets([
    'generated_input_1',
    'generated_input_2',
  ]);
  const clientSeedRules = collectBullets([
    'generated_client_seed_rule_1',
    'generated_client_seed_rule_2',
    'generated_client_seed_rule_3',
  ]);
  const roundStartSteps = collectBullets([
    'generated_round_step_1',
    'generated_round_step_2',
    'generated_round_step_3',
  ]);

  const heading100 = (t(`${PF}.heading_100`) as string).trim();
  const generatedInputsIntro = (
    t(`${PF}.generated_inputs_intro`, { defaultValue: '' }) as string
  ).trim();
  const clientSeedRulesIntro = (
    t(`${PF}.generated_client_seed_rules_intro`, { defaultValue: '' }) as string
  ).trim();
  const generatedInputsCombined = (
    t(`${PF}.generated_inputs_combined_paragraph`, {
      defaultValue: '',
    }) as string
  ).trim();
  const generatedCommitment = (
    t(`${PF}.generated_commitment_paragraph`, { defaultValue: '' }) as string
  ).trim();
  const generatedRoundStartTitle = (
    t(`${PF}.generated_round_start_title`, { defaultValue: '' }) as string
  ).trim();
  const generatedClosing = (
    t(`${PF}.generated_round_closing`, { defaultValue: '' }) as string
  ).trim();

  const topIconUrl = headerIconUrl ?? modalIconUrl;
  const useCustomHeaderIcon = !!headerIconUrl;

  const bulletListStyle: React.CSSProperties = {
    ...styles.modalText,
    listStyleType: 'disc',
    paddingLeft: '1.25rem',
    margin: '8px 0 0',
    textAlign: 'left',
  };

  const modalCloseIcon = (styles as { modalCloseIcon?: React.CSSProperties })
    .modalCloseIcon;

  if (!isOpen) return null;

  const panelStyle: React.CSSProperties = {
    ...styles.modalContent,
    transform: visible ? 'translateY(0)' : 'translateY(-100vh)',
    opacity: visible ? 1 : 0,
    transition:
      'transform 0.5s cubic-bezier(0.25, 1, 0.3, 1), opacity 0.4s ease',
    display: 'flex',
    flexDirection: 'column',
  };

  const panelBody = (
    <>
      <style>{PF_HEADER_WALLET_RESPONSIVE_CSS}</style>
      <div
        data-pf-header
        style={{
          ...styles.modalHeaderContainer,
          position: 'relative',
          zIndex: 2,
          flexWrap: 'wrap-reverse',
          rowGap: '8px',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          ...(isScrolled ? styles.modalHeaderScrollActive : {}),
        }}
      >
        <h2 style={styles.modalHeader}>{t(`${PF}.what_is_title`)}</h2>
        <div
          data-pf-header-actions
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginLeft: 'auto',
          }}
        >
          <WalletBalance variant="modal" />
          <button
            type="button"
            className={modalCloseIcon ? 'autoplay-3d-btn' : undefined}
            data-skyx-modal-close
            style={{
              ...styles.modalCloseButton,
              ...(isHovered ? styles.modalCloseButtonHover : {}),
            }}
            onClick={onClose}
            aria-label={t('ui.common.close')}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {modalCloseIcon ? (
              <span
                style={{
                  ...modalCloseIcon,
                  opacity: isHovered ? 0.85 : 1,
                }}
                aria-hidden
              >
                ×
              </span>
            ) : (
              <>×</>
            )}
          </button>
        </div>
      </div>

      <div
        className="bet-list-scrollbar"
        onScroll={e => setIsScrolled(e.currentTarget.scrollTop > 0)}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: '100%',
          flexGrow: 1,
        }}
      >
        <div style={styles.modalBody}>
          <div style={styles.topSection} data-pf-section="hero">
            <img
              src={topIconUrl}
              alt={t(`${PF}.icon_alt`)}
              style={{
                width: useCustomHeaderIcon ? '72px' : '80px',
                height: useCustomHeaderIcon ? '72px' : '90px',
                objectFit: 'contain',
                margin: '0 0 10px 0',
                ...(useCustomHeaderIcon
                  ? {}
                  : {
                      filter:
                        'invert(56%) sepia(99%) saturate(680%) hue-rotate(74deg) brightness(98%) contrast(101%)',
                    }),
              }}
            />
            {heading100 ? (
              <h3
                style={{
                  color: '#fff',
                  fontSize: '14px',
                  margin: '0 0 10px 0',
                  fontWeight: 400,
                }}
                data-pf-hero-title
              >
                {heading100}
              </h3>
            ) : null}
            <p style={styles.highlightedParagraph}>
              {t(`${PF}.intro_paragraph`)}
            </p>
            {introBullets.length > 0 ? (
              <ul style={bulletListStyle}>
                {introBullets.map((line, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom: i === introBullets.length - 1 ? 0 : '6px',
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div data-pf-section="body">
            <h3 style={styles.sectionTitle}>{t(`${PF}.how_it_works`)}</h3>
            <h4 style={styles.subTitle}>{t(`${PF}.quick_explanation`)}</h4>
            {quickBullets.length > 0 ? (
              <ul style={bulletListStyle}>
                {quickBullets.map((line, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom: i === quickBullets.length - 1 ? 0 : '6px',
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={styles.modalText}>
                {t(`${PF}.quick_explanation_text`)}
              </p>
            )}

            <h4 style={styles.subTitle}>{t(`${PF}.more_information`)}</h4>
            {generatedInputsIntro ? (
              <p style={styles.modalText}>{generatedInputsIntro}</p>
            ) : null}
            {generatedInputs.length > 0 ? (
              <ul style={bulletListStyle}>
                {generatedInputs.map((line, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom:
                        i === generatedInputs.length - 1 ? 0 : '6px',
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <p style={styles.modalText}>
                  {t(`${PF}.more_information_text_1`)}
                </p>
                <p style={styles.modalText}>
                  {t(`${PF}.more_information_text_2`)}
                </p>
              </>
            )}
            {clientSeedRulesIntro ? (
              <p style={styles.modalText}>{clientSeedRulesIntro}</p>
            ) : null}
            {clientSeedRules.length > 0 ? (
              <ul style={bulletListStyle}>
                {clientSeedRules.map((line, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom:
                        i === clientSeedRules.length - 1 ? 0 : '6px',
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            ) : null}
            {generatedInputsCombined ? (
              <p style={styles.modalText}>{generatedInputsCombined}</p>
            ) : null}
            {generatedCommitment ? (
              <p style={styles.modalText}>{generatedCommitment}</p>
            ) : null}
            {generatedRoundStartTitle ? (
              <p style={{ ...styles.modalText, marginBottom: '6px' }}>
                {generatedRoundStartTitle}
              </p>
            ) : null}
            {roundStartSteps.length > 0 ? (
              <ol
                style={{
                  ...bulletListStyle,
                  listStyleType: 'decimal',
                  paddingLeft: '1.5rem',
                }}
              >
                {roundStartSteps.map((line, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom:
                        i === roundStartSteps.length - 1 ? 0 : '6px',
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ol>
            ) : null}
            {generatedClosing ? (
              <p style={styles.modalText}>{generatedClosing}</p>
            ) : null}
          </div>

          <div
            style={{
              ...styles.diagramSection,
              ...(i18nGameId === 'skyx_new'
                ? { display: 'block', alignSelf: 'stretch' }
                : {}),
            }}
            data-pf-section="diagram"
          >
            <div
              style={{
                width: '100%',
                minWidth: 0,
                display: 'block',
                boxSizing: 'border-box',
              }}
            >
              {diagramOverride ?? (
                <img
                  src={diagramImageUrl}
                  alt={t(`${PF}.diagram_alt`)}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              )}
            </div>
          </div>

          <div data-pf-section="checklist">
            <h3 style={styles.sectionTitle}>{t(`${PF}.how_to_check`)}</h3>
            <ul style={styles.howToCheckList}>
              <li style={{ marginBottom: '8px' }}>
                {t(`${PF}.how_to_check_item_1`)}
              </li>
              <li style={{ marginBottom: '8px' }}>
                {t(`${PF}.how_to_check_item_2`)}
              </li>
              <li style={{ marginBottom: '8px' }}>
                {t(`${PF}.how_to_check_item_3`)}
              </li>
              <li style={{ marginBottom: 0 }}>
                {t(`${PF}.how_to_check_item_4`)}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );

  const modal = (
    <div
      style={{
        ...styles.modalOverlay,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.25s ease',
      }}
      data-skyx-modal-backdrop
      onClick={onClose}
    >
      {modalCloseIcon ? (
        <SkyxNewModalPanelShell
          panelStyle={panelStyle}
          data-skyx-modal-panel
          data-skyx-pf-info
          onClick={e => e.stopPropagation()}
        >
          {panelBody}
        </SkyxNewModalPanelShell>
      ) : (
        <div
          style={panelStyle}
          data-skyx-modal-panel
          onClick={e => e.stopPropagation()}
        >
          {panelBody}
        </div>
      )}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};
